import { FC, useCallback, useMemo } from 'react'

import { VStack } from '@revolut-internal/ui-kit'

import { ChartTableSkeleton } from 'components/ChartTableSkeleton'
import { LineChart } from 'components/LineChart/LineChart'
import { PageSection } from 'components/PageSection/PageSection'
import { TabBar } from 'components/TabBar/TabBar'
import { useChannel } from 'hooks/useChannel'
import { usePrepareChartColorMap } from 'hooks/usePrepareChartColorMap'
import { useSelectedCharts } from 'hooks/useSelectedCharts'
import { useTimeRangeParams } from 'hooks/useTimeRangeParams'
import { useAgentAttendanceIntervalsQuery } from 'modules/api/analytics/queries'
import { UrlStateStorageKeys } from 'modules/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/url-state-storage/useHashedSearchParams'
import { GlobalFilterState } from 'pages/Monitoring/GlobalFilters/GlobalFilters'
import { SpecialisationAttendanceAggregated } from 'types'
import { prepareChartData } from 'utils/charts/prepareChartData'
import { convertTimeStringToDate } from 'utils/time/convertTimeStringToDate'

import { AgentAttendanceIntervalsTable } from './AgentAttendanceIntervalsTable'
import { AttendanceVariant, AttendanceVariants } from './types'

const ATTENDANCE_INITIAL_STATE = {
  attendanceVariant: AttendanceVariants.Scheduled,
}

export const AgentAttendanceIntervals: FC = () => {
  const { channel, regionedChannel } = useChannel()

  const { state } = useHashedSearchParams<GlobalFilterState>({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })
  const {
    state: { attendanceVariant },
    updateState,
  } = useHashedSearchParams<{ attendanceVariant: AttendanceVariant }>({
    hashParamKey: UrlStateStorageKeys.AgentIntervalAttendanceControls,
    initialState: ATTENDANCE_INITIAL_STATE,
  })
  const { from, to, start, end } = useTimeRangeParams({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })
  const { selectedCharts, handleChartsSelect } = useSelectedCharts(
    UrlStateStorageKeys.AgentIntervalAttendanceTableSelectedCharts,
  )

  const queueId = state.queues
  const agentId = state.agents
  const specialisationId = state.specialisations
  const departmentId = state.departments
  const areaId = state.areas

  const { data: agentAttendanceIntervals = [], isPending } =
    useAgentAttendanceIntervalsQuery({
      from,
      to,
      start: convertTimeStringToDate({
        timeStr: start,
        useUtc: true,
        shouldReturnDate: false,
        shouldReturnDefaultTime: false,
      }),
      end: convertTimeStringToDate({
        timeStr: end,
        useUtc: true,
        shouldReturnDate: false,
        shouldReturnDefaultTime: false,
      }),
      departmentId,
      areaId,
      specialisationId,
      queueId,
      agentId,
      channel: channel ? [channel] : [],
    })

  const getDataKey = useCallback(
    (data: SpecialisationAttendanceAggregated) => data.specialisation.name,
    [],
  )

  const { colorMap } = usePrepareChartColorMap(getDataKey, agentAttendanceIntervals)

  const scheduledChart = useMemo(
    () =>
      attendanceVariant === AttendanceVariants.Scheduled
        ? prepareChartData({
            data: agentAttendanceIntervals,
            dataKey: getDataKey,
            dataCountKey: 'scheduled',
            colorMap,
          })
        : undefined,
    [agentAttendanceIntervals, attendanceVariant, getDataKey, colorMap],
  )
  const adherenceChart = useMemo(
    () =>
      attendanceVariant === AttendanceVariants.Adherence
        ? prepareChartData({
            data: agentAttendanceIntervals,
            dataKey: getDataKey,
            dataCountKey: 'adherence',
            colorMap,
          })
        : undefined,
    [agentAttendanceIntervals, attendanceVariant, getDataKey, colorMap],
  )
  const chartData = scheduledChart?.chartData || adherenceChart?.chartData || []
  const chartLines = scheduledChart?.charts || adherenceChart?.charts || []
  const chartTooltipLabel =
    attendanceVariant === AttendanceVariants.Scheduled ? 'agent' : '%'

  const handleAttendanceChange = (newAttendanceVariant: AttendanceVariant | null) => {
    if (!newAttendanceVariant) {
      return
    }

    updateState({
      attendanceVariant: newAttendanceVariant,
    })
  }

  return (
    <PageSection
      title="Interval attendance"
      side={
        <TabBar
          behaviourVariant="action"
          variant="segmented fit"
          width={300}
          tabs={[
            { value: AttendanceVariants.Scheduled, label: 'Scheduled' },
            { value: AttendanceVariants.Adherence, label: 'Adherence' },
          ]}
          value={attendanceVariant}
          onChange={handleAttendanceChange}
        />
      }
    >
      {isPending ? (
        <ChartTableSkeleton />
      ) : (
        <VStack space="s-32">
          <LineChart
            data={chartData}
            charts={chartLines}
            selectedCharts={selectedCharts}
            primaryLabel={chartTooltipLabel}
            entityName="Specialisation"
          />

          <AgentAttendanceIntervalsTable
            data={chartData}
            charts={chartLines}
            attendanceVariant={attendanceVariant}
            selectedCharts={selectedCharts}
            onSelectCharts={handleChartsSelect}
          />
        </VStack>
      )}
    </PageSection>
  )
}
